import styles from "./styles.module.scss";
import { FC, Props } from "./typings";
import { useEffect, useState } from "react";
import Button from "components/Button";
import HollowButton from "components/HollowButton";
import activationService from "api/activationService";
import Spinner from "components/Spinner";
import { getErrorMessage } from "utils/getErrorMessage";
import { IMobywatelQRCodeResponse } from "dto/Activation/IMobywatelQRCodeResponse";
import { ENotificationType, notification } from "utils/notification";
import { QRCodeCanvas } from "qrcode.react";
import ReactDOM from "react-dom";
import { HubCallbacksNames, IUserIdentified } from "api/hub";
import MobywatelIcon from "assets/icons/pi.svg?react";
import contactFormVerificationService from "api/contactFormVerificationService";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

export enum IdentityMethod {
  Signature = "signature",
  LoginGov = "loginGov",
  Mobywatel = "mObywatel",
}

export enum IdentityStep {
  MObywatelQRCode = "mobywatelQRCode",
  Success = "success",
}

const Identity: FC<Props> = ({ SignalRContext, prevStep }) => {
  const { t } = useTranslation();
  const { key } = useParams();

  SignalRContext.useSignalREffect(
    HubCallbacksNames.UserIdentified,
    (message: IUserIdentified) => {
      if (message.success) {
        // todo: request do BE

        setErrorMessage("");
        setStep(IdentityStep.Success);
      } else {
        notification({
          title: t("identity.verificationFailed"),
          text: message.message,
          type: ENotificationType.ERROR,
        });
      }
    },
    []
  );

  const [method, setMethod] = useState<IdentityMethod | null>(null);
  const [step, setStep] = useState<IdentityStep | null>(null);
  const [allowBack, setAllowBack] = useState<boolean>(true);
  const [prevButtonState, setPrevButtonState] = useState({
    show: true,
    label: t("common.back"),
    disabled: false,
  });

  const [nextButtonState, setNextButtonState] = useState({
    show: false,
    label: "",
    disabled: false,
  });
  const [, setErrorMessage] = useState<string>(null);
  const [qrCodeData, setQRCodeData] = useState<string>("");

  activationService.useGetStatement();
  activationService.useUploadStatement();

  const { mutate: getMobywatelQRCode, isLoading: isLoadingQRCode } =
    contactFormVerificationService.useGetMObywatelQRcode();

  const initMObywatelQRCode = () => {
    getMobywatelQRCode(
      {
        key: key,
        connectionId: SignalRContext.connection.connectionId,
      },
      {
        onSuccess(data) {
          const resp = data.data as IMobywatelQRCodeResponse;
          setQRCodeData(resp.Raw);
        },
        onError(e: any) {
          notification({
            title: t("errors.somethingWentWrong"),
            text: getErrorMessage(e.response.data),
            type: ENotificationType.ERROR,
          });
        },
      }
    );
  };

  useEffect(() => {
    switch (method) {
      case IdentityMethod.Mobywatel:
        setStep(IdentityStep.MObywatelQRCode);
        setNextButtonState({
          ...nextButtonState,
          disabled: false,
          show: false,
        });
        setPrevButtonState({ ...prevButtonState, disabled: false, show: true });
        initMObywatelQRCode();
        setAllowBack(true);
        break;
      default:
        setNextButtonState({
          ...nextButtonState,
          disabled: false,
          show: false,
        });
        setPrevButtonState({
          ...prevButtonState,
          disabled: false,
          show: false,
        });
        setAllowBack(true);
    }
  }, [method]);

  useEffect(() => {
    switch (step) {
      case IdentityStep.Success:
        setNextButtonState({
          ...nextButtonState,
          disabled: false,
          show: true,
          label: t("common.next"),
        });
        setPrevButtonState({
          ...prevButtonState,
          show: false,
        });
        setAllowBack(false);
        break;
    }
  }, [step]);

  useEffect(() => {
    if (qrCodeData && document.getElementById("qrcode")) {
      ReactDOM.render(
        <QRCodeCanvas value={qrCodeData} size={256} />,
        document.getElementById("qrcode")
      );
    }
  }, [qrCodeData]);

  const prevStepInternal = () => {
    if (!method) {
      prevStep();
      return;
    }

    switch (step) {
      case IdentityStep.MObywatelQRCode:
        setMethod(null);
    }
  };

  return (
    <>
      {allowBack && (
        <div className={styles.backTop}>
          {" "}
          <HollowButton
            text={t("common.backArrow")}
            onClick={() => prevStepInternal?.()}
          ></HollowButton>
        </div>
      )}
      <div className={styles.wrapper}>
        {!method && (
          <>
            <div className={styles.header}>{t("identity.verifyTitle")}</div>
            <div className={styles.subheader}>
              {t("identity.verifyMessage")}
            </div>
            <div className={styles.methods}>
              <div
                className={styles.method}
                onClick={() => setMethod(IdentityMethod.Mobywatel)}
              >
                <div className={styles.icon}>
                  <MobywatelIcon></MobywatelIcon>
                </div>
                {t("identity.mobywatelApp")}
              </div>
            </div>
          </>
        )}

        {method === IdentityMethod.Mobywatel &&
          step === IdentityStep.MObywatelQRCode && (
            <>
              <div className={styles.header}>{t("identity.verifyTitle")}</div>
              <div className={styles.hint}>
                <b>{t("identity.scanQRCode")}</b>
                <br></br>
                {t("identity.mobywatelDesktop")}
                <br></br>
                {t("identity.requiredData")}
              </div>
              <div id="qrcode" className={styles.qrcode}></div>
              {isLoadingQRCode && (
                <div className={styles.loading}>
                  <Spinner className={styles.spinner} />
                  <div>{t("common.loading")}</div>
                </div>
              )}
            </>
          )}

        {step === IdentityStep.Success && (
          <>
            <div className={styles.wrapper}>
              <div className={styles.header}>
                <div className={styles.title}>
                  {t("identity.requestVerification")}
                </div>
                <div className={styles.successWrapper}>
                  <div className={styles.success2}>
                    {t("identity.requestAccepted")}
                  </div>
                </div>

                <div className={styles.side2}>
                  {t("identity.requestThankYou")}
                </div>
              </div>
              <div className={styles.text}></div>
              <div className={styles.text}>
                {t("identity.requestEmailInfo")}
              </div>

              <div className={styles.buttons}>
                <Button
                  className={styles.button}
                  text={t("common.finish")}
                  onClick={() => (window.location.href = "https://notaup.pl")}
                ></Button>
              </div>
            </div>
          </>
        )}

        <div className={styles.buttons}></div>
      </div>
    </>
  );
};

export default Identity;
